import * as stylex from '@stylexjs/stylex'
import { memo, useMemo, type ComponentPropsWithoutRef, type ElementType, type ForwardedRef, type ReactNode } from 'react'
import isEqual from 'react-fast-compare'
import { TableContext, useTableContext, type TableContextValue } from '../Table/TableContext'
import { makeExtendStyle, type ExtendStyleProps } from '../utils/makeExtendStyle/makeExtendStyle'

const _baseStyles = stylex.create({
  root: {
    display: 'table-row-group',
  },
})

export type TableBodyProps<RootComponent extends ElementType = 'tbody', TableBodyElement extends HTMLElement = HTMLTableSectionElement> = {
  /** 自訂根元素標籤 */
  'component'?: RootComponent
  /** 子內容（通常為 TableRow） */
  'children'?: ReactNode
  /** 自訂根元素的 StyleX 樣式 */
  'rootStyle'?: stylex.StyleXStyles
  /** 根元素的 ref */
  'rootRef'?: ForwardedRef<TableBodyElement>
  /** 測試用 data 屬性 */
  'data-testid'?: string
} & ExtendStyleProps & ComponentPropsWithoutRef<RootComponent>

const TableBodyComponent = <RootComponent extends ElementType = 'tbody', TableBodyElement extends HTMLElement = HTMLTableSectionElement>(
  props: TableBodyProps<RootComponent, TableBodyElement>,
) => {
  const { component = 'tbody', extendStyles, rootStyle, rootRef, ...rest } = makeExtendStyle(props)
  const Component = component as ElementType
  const defaultComponent = 'tbody'
  const parentTableContext = useTableContext()

  const tableContextValue: TableContextValue = useMemo(
    () => ({
      ...parentTableContext,
      variant: 'body',
    }),
    [parentTableContext],
  )

  return (
    <TableContext.Provider value={tableContextValue}>
      <Component
        ref={rootRef}
        role={component === defaultComponent ? undefined : 'rowgroup'}
        {...stylex.props(_baseStyles.root, rootStyle, extendStyles)}
        {...rest}
      />
    </TableContext.Provider>
  )
}

/**
 * TableBody 是表格主體區域元件，透過 context 將 variant 設為 'body'。
 */
export const TableBody = memo(TableBodyComponent, isEqual) as typeof TableBodyComponent
